import gql from 'graphql-tag';
import React, {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import {useActiveThread, useAgentChatDispatch} from '../../agentChat/store';
import {
  resolveRunDisplayNames,
  type RunDisplayNameResolution,
} from '../../agentChat/store/actions';
import {
  createRunDisplayNameKey,
  type RunDisplayNameKeyParts,
} from '../../agentChat/store/runDisplayNameKey';
import {RUN_SEARCH_QUERY} from '../../state/graphql/runSearchQuery';
import {useApolloClient} from '../../state/hooks';
import {runNamesFilter, toMongo} from '../../util/filters';

type ProjectQueue = {
  entityName: string;
  projectName: string;
  runNames: Set<string>;
};

type EnqueueRunDisplayNames = (
  threadId: string,
  runs: RunDisplayNameKeyParts[]
) => void;

interface RunNode {
  __typename?: 'Run';
  id: string;
  name: string;
  displayName: string | null;
}

interface RunSearchResult {
  project: {
    runs: {
      edges: Array<{node: RunNode}>;
    };
  } | null;
}

/**
 * Fragment used to read a Run's latest displayName out of the Apollo cache
 * by its normalized cache id.
 */
const RUN_DISPLAY_NAME_FRAGMENT = gql`
  fragment WBAgentRunDisplayName on Run {
    id
    name
    displayName
  }
`;

const WBAgentRunDisplayNameContext = createContext<EnqueueRunDisplayNames>(
  () => {}
);

export function WBAgentRunDisplayNameProvider({
  children,
}: {
  children: ReactNode;
}) {
  const dispatch = useAgentChatDispatch();
  const client = useApolloClient();
  const activeThread = useActiveThread();

  // Pending batches, grouped by thread then by project.
  const queueRef = useRef<Map<string, Map<string, ProjectQueue>>>(new Map());
  // {(entity/project/runName): Apollo cache id} map. Acts as a name=>id index so we can
  // hit Apollo's normalized cache (which is keyed by GraphQL `id`, not name).
  const runIdByKeyRef = useRef<Map<string, string>>(new Map());
  // Run keys confirmed not to exist; prevents repeated lookups.
  const notFoundRef = useRef<Set<string>>(new Set());
  // Run keys currently queued or in-flight, to avoid duplicate batching.
  const inFlightRef = useRef<Set<string>>(new Set());

  const flush = useCallback(async () => {
    const queue = queueRef.current;
    queueRef.current = new Map();

    for (const [threadId, projectQueues] of queue) {
      const results: Record<string, RunDisplayNameResolution> = {};

      await Promise.all(
        Array.from(projectQueues.values()).map(async projectQueue => {
          const requestedRunNames = Array.from(projectQueue.runNames);
          const keyForRunName = (runName: string) =>
            createRunDisplayNameKey({
              entityName: projectQueue.entityName,
              projectName: projectQueue.projectName,
              runName,
            });

          try {
            const {data} = await client.query<RunSearchResult>({
              query: RUN_SEARCH_QUERY,
              variables: {
                entityName: projectQueue.entityName,
                projectName: projectQueue.projectName,
                filters: JSON.stringify(
                  toMongo(runNamesFilter(requestedRunNames))
                ),
                first: requestedRunNames.length,
              },
            });
            const returnedRunNames = new Set<string>();
            for (const edge of data.project?.runs?.edges ?? []) {
              const node = edge.node;
              const key = keyForRunName(node.name);
              returnedRunNames.add(node.name);
              // Matches Apollo's default `dataIdFromObject` of `${__typename}:${id}`.
              runIdByKeyRef.current.set(key, `Run:${node.id}`);
              results[key] = {
                status: 'resolved',
                displayName: node.displayName ?? node.name,
              };
            }
            for (const requestedRunName of requestedRunNames) {
              if (!returnedRunNames.has(requestedRunName)) {
                const key = keyForRunName(requestedRunName);
                notFoundRef.current.add(key);
                results[key] = {status: 'not-found'};
              }
            }
          } catch {
            // On error, leave inFlight cleared so a future enqueue retries.
            for (const requestedRunName of requestedRunNames) {
              results[keyForRunName(requestedRunName)] = {status: 'error'};
            }
          } finally {
            for (const requestedRunName of requestedRunNames) {
              inFlightRef.current.delete(keyForRunName(requestedRunName));
            }
          }
        })
      );

      if (Object.keys(results).length > 0) {
        dispatch(resolveRunDisplayNames(threadId, results));
      }
    }
  }, [client, dispatch]);

  const enqueue = useCallback<EnqueueRunDisplayNames>(
    (threadId, runs) => {
      const cachedResults: Record<string, RunDisplayNameResolution> = {};
      let queuedAny = false;

      for (const run of runs) {
        const key = createRunDisplayNameKey(run);

        if (notFoundRef.current.has(key)) {
          cachedResults[key] = {status: 'not-found'};
          continue;
        }

        const cacheId = runIdByKeyRef.current.get(key);
        if (cacheId) {
          const cached = client.readFragment<{
            name: string;
            displayName: string | null;
          }>({
            id: cacheId,
            fragment: RUN_DISPLAY_NAME_FRAGMENT,
          });
          if (cached) {
            cachedResults[key] = {
              status: 'resolved',
              displayName: cached.displayName ?? cached.name,
            };
            continue;
          }
          // Apollo evicted the entity; drop the stale pointer and re-batch.
          runIdByKeyRef.current.delete(key);
        }

        if (inFlightRef.current.has(key)) {
          continue;
        }
        inFlightRef.current.add(key);
        queuedAny = true;

        let threadQueue = queueRef.current.get(threadId);
        if (!threadQueue) {
          threadQueue = new Map();
          queueRef.current.set(threadId, threadQueue);
        }

        const projectKey = `${run.entityName}/${run.projectName}`;
        let projectQueue = threadQueue.get(projectKey);
        if (!projectQueue) {
          projectQueue = {
            entityName: run.entityName,
            projectName: run.projectName,
            runNames: new Set(),
          };
          threadQueue.set(projectKey, projectQueue);
        }
        projectQueue.runNames.add(run.runName);
      }

      if (Object.keys(cachedResults).length > 0) {
        dispatch(resolveRunDisplayNames(threadId, cachedResults));
      }

      if (queuedAny) {
        void flush();
      }
    },
    [client, dispatch, flush]
  );

  // Eagerly resolve every run mention across the active historical thread,
  // and refresh any already-resolved mentions whose cached displayName
  // has drifted (e.g. the run's display name was edited).
  useEffect(() => {
    if (!activeThread) {
      return;
    }
    const pendingRuns: RunDisplayNameKeyParts[] = [];
    const refreshes: Record<string, RunDisplayNameResolution> = {};

    for (const node of Object.values(activeThread.messageTree.nodes)) {
      for (const p of node.content) {
        if (p.type !== 'wandb-run') {
          continue;
        }
        if (
          p.runDisplayNameStatus === 'pending' ||
          p.runDisplayNameStatus === 'error'
        ) {
          pendingRuns.push({
            entityName: p.entityName,
            projectName: p.projectName,
            runName: p.runName,
          });
          continue;
        }
        if (p.runDisplayNameStatus !== 'resolved') {
          continue;
        }
        // For resolved run parts, re-read Apollo's cache to catch any
        // renames since this thread was last visible.
        const key = createRunDisplayNameKey(p);
        const cacheId = runIdByKeyRef.current.get(key);
        if (!cacheId) {
          continue;
        }
        const cached = client.readFragment<{
          name: string;
          displayName: string | null;
        }>({id: cacheId, fragment: RUN_DISPLAY_NAME_FRAGMENT});
        if (!cached) {
          // Apollo evicted the entity; drop the dangling pointer so the next
          // enqueue will re-fetch.
          runIdByKeyRef.current.delete(key);
          continue;
        }
        const fresh = cached.displayName ?? cached.name;
        if (fresh !== p.runDisplayName) {
          refreshes[key] = {status: 'resolved', displayName: fresh};
        }
      }
    }

    if (Object.keys(refreshes).length > 0) {
      dispatch(resolveRunDisplayNames(activeThread.id, refreshes));
    }
    if (pendingRuns.length > 0) {
      enqueue(activeThread.id, pendingRuns);
    }
  }, [activeThread, enqueue, client, dispatch]);

  return (
    <WBAgentRunDisplayNameContext.Provider value={enqueue}>
      {children}
    </WBAgentRunDisplayNameContext.Provider>
  );
}
